/**
 * Signmakerz spam-gate — shared 4-condition client-side spam filter.
 *
 * Owns window.leadFormState and the scroll listener. Every lead-form click
 * handler in the theme calls:
 *
 *   SpamGate.resetLeadFormState();
 *   if (SpamGate.isSpamSubmission(cfg)) {
 *     SpamGate.submitFormB(form, cfg);
 *   } else {
 *     submitForm(form);
 *   }
 *
 * The 4 conditions (all must be true to route to /api/submit-form):
 *   1. scrollZero      — visitor never scrolled during this fill cycle
 *   2. allSignTypes    — every sign-type checkbox in signTypeSelector is checked
 *   3. bothPlacements  — every id in placementIds is checked
 *   4. fastFill        — Date.now() - formOpenedAt < fastFillMs (default 35 s)
 *   plus: noAds        — no gclid/fbclid/utm_* in current URL, entry_url, or first_click
 *
 * resetLeadFormState() MUST be called at the start of every submit click handler,
 * otherwise the gate silently disables after 35 s on the same page instance
 * (theme uses pushState after submit, so formOpenedAt would otherwise stick).
 */
(function () {
  'use strict';

  window.leadFormState = window.leadFormState || { formOpenedAt: Date.now(), maxScroll: 0 };

  function getScrollDepth() {
    var h = document.documentElement;
    var denom = h.scrollHeight || 1;
    return Math.min(100, Math.round(((h.scrollTop + window.innerHeight) / denom) * 100));
  }

  window.addEventListener('scroll', function () {
    var d = getScrollDepth();
    if (d > window.leadFormState.maxScroll) window.leadFormState.maxScroll = d;
  }, { passive: true });

  var AD_PARAM_KEYS = ['gclid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign'];

  function hasAdParams() {
    try {
      var urls = [window.location.href];
      var storage = window.safeLocalStorage || localStorage;
      var entry = storage.getItem('entry_url'); if (entry) urls.push(entry);
      var firstClick = storage.getItem('first_click'); if (firstClick) urls.push(firstClick);
      for (var i = 0; i < urls.length; i++) {
        var u = (urls[i] || '').toLowerCase();
        for (var k = 0; k < AD_PARAM_KEYS.length; k++) {
          if (u.indexOf(AD_PARAM_KEYS[k]) !== -1) return true;
        }
      }
    } catch (e) {}
    return false;
  }

  /**
   * cfg fields:
   *   signTypeSelector  CSS selector for sign-type checkboxes.
   *                     Default '#checkboxContainer input[type=checkbox]'.
   *   placementIds      Array of element ids that must all be checked.
   *                     Default ['Indoor', 'Outdoor'].
   *   fastFillMs        Milliseconds cutoff for fastFill. Default 35000.
   *
   * Returns a diagnostic object:
   *   {
   *     isSpam:  boolean — true iff ALL 5 checks pass
   *     checks:  { scrollZero, allSignTypes, bothPlacements, fastFill, noAds } booleans
   *     state:   { maxScroll, elapsedMs, signTypesTotal, signTypesChecked, placements, adParamsFound }
   *   }
   *
   * Use isSpamSubmission(cfg) for a simple boolean (kept for callers that don't
   * need the diagnostic detail).
   */
  function evaluateSpam(cfg) {
    cfg = cfg || {};
    var state = window.leadFormState || { formOpenedAt: Date.now(), maxScroll: 0 };
    var maxScroll = state.maxScroll;
    var elapsedMs = Date.now() - state.formOpenedAt;

    var scrollZero = maxScroll === 0;

    var selector = cfg.signTypeSelector || '#checkboxContainer input[type=checkbox]';
    var signBoxes = document.querySelectorAll(selector);
    var checkedCount = 0;
    for (var i = 0; i < signBoxes.length; i++) if (signBoxes[i].checked) checkedCount++;
    var allSignTypes = signBoxes.length > 0 && checkedCount === signBoxes.length;

    var placementIds = cfg.placementIds || ['Indoor', 'Outdoor'];
    var placementsReport = [];
    var bothPlacements = placementIds.length > 0;
    for (var j = 0; j < placementIds.length; j++) {
      var el = document.getElementById(placementIds[j]);
      var isChecked = !!(el && el.checked);
      placementsReport.push({ id: placementIds[j], checked: isChecked });
      if (!isChecked) bothPlacements = false;
    }

    var fastFillMs = cfg.fastFillMs || 35000;
    var fastFill = elapsedMs < fastFillMs;

    var adParamsFound = hasAdParams();
    var noAds = !adParamsFound;

    return {
      isSpam: scrollZero && allSignTypes && bothPlacements && fastFill && noAds,
      checks: {
        scrollZero: scrollZero,
        allSignTypes: allSignTypes,
        bothPlacements: bothPlacements,
        fastFill: fastFill,
        noAds: noAds,
      },
      state: {
        maxScroll: maxScroll,
        elapsedMs: elapsedMs,
        signTypesTotal: signBoxes.length,
        signTypesChecked: checkedCount,
        placements: placementsReport,
        adParamsFound: adParamsFound,
      },
    };
  }

  function isSpamSubmission(cfg) {
    return evaluateSpam(cfg).isSpam;
  }

  // Compact key/value codes so the meta payload doesn't leak "failed_checks",
  // "scrollZero", etc. to a network sniffer. Decoding table (server-side):
  //   Outer:  g=gate, t=template, r=result, f=failed_checks, s=state
  //   Result: n1=passed_to_normal_endpoint, s1=spam_routed_to_/api/submit-form
  //   Checks: sz=scrollZero, at=allSignTypes, bp=bothPlacements, ff=fastFill, na=noAds
  //   State:  m=maxScroll(%), e=elapsedMs, st=signTypesTotal, sc=signTypesChecked,
  //           a=adParamsFound(1/0)
  var CHECK_CODE = {
    scrollZero: 'sz',
    allSignTypes: 'at',
    bothPlacements: 'bp',
    fastFill: 'ff',
    noAds: 'na',
  };

  /**
   * Attach a hidden input `_ck` to the form so `/api/no-approval` receives a
   * JSON diagnostic explaining which of the 4 checks failed. Field name and
   * inner keys are deliberately terse (see CHECK_CODE above and the outer-key
   * comment) so a passive traffic capture doesn't reveal the detection logic
   * in cleartext, but the value is plain JSON so backend decode is trivial:
   *   json_decode($request->input('_ck'), true)
   *
   * evaluation: object returned by evaluateSpam().
   * templateHint (optional): short identifier for the calling template.
   */
  function attachDiagnosticMeta(form, evaluation, templateHint) {
    try {
      var f = [];
      for (var k in evaluation.checks) {
        if (!evaluation.checks[k]) f.push(CHECK_CODE[k] || k);
      }
      var payload = {
        g: 'c1',
        t: templateHint || '',
        r: evaluation.isSpam ? 's1' : 'n1',
        f: f,
        s: {
          m: evaluation.state.maxScroll,
          e: evaluation.state.elapsedMs,
          st: evaluation.state.signTypesTotal,
          sc: evaluation.state.signTypesChecked,
          a: evaluation.state.adParamsFound ? 1 : 0,
        },
      };
      var existing = form.querySelector('input[name="_ck"]');
      if (existing) existing.remove();
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = '_ck';
      input.value = JSON.stringify(payload);
      form.appendChild(input);
    } catch (e) {
      console.error('SpamGate.attachDiagnosticMeta error:', e);
    }
  }

  /**
   * cfg fields:
   *   endpoint       POST target. Default 'https://submissions.signmakerz.com/api/submit-form'.
   *   spamReason     Free-form string appended as `spam_reason` — used to identify
   *                  which template triggered the gate.
   *   emailFieldId   DOM id of the email input, used for x-form-signature.
   *                  Default 'email'.
   *   nameFieldId    DOM id of the name input, used for x-form-signature.
   *                  Default 'name'.
   *   onDone         Callback fired after xhr.onload/onerror. Receives (form).
   *                  Typical use: show the thank-you screen so the bot can't
   *                  tell it was filtered.
   */
  function submitFormB(form, cfg) {
    cfg = cfg || {};

    var formData = new FormData(form);
    // Field names deliberately terse so a passive traffic capture doesn't reveal
    // detection state in cleartext. Server-side decoding table:
    //   _x = flag (always "1" from submitFormB)
    //   _r = reason code (short template identifier: a/b/c or "g1" default)
    formData.append('_x', '1');
    formData.append('_r', cfg.spamReason || 'g1');
    try { formData.append('lead_page_url', window.location.href); } catch (e) {}
    try {
      var storage = window.safeLocalStorage || localStorage;
      formData.append('entry_url', storage.getItem('entry_url') || '');
      formData.append('first_click', storage.getItem('first_click') || '');
      formData.append('user_uuid', storage.getItem('user_uuid') || '');
    } catch (e) {}

    var xhr = new XMLHttpRequest();
    xhr.open('POST', cfg.endpoint || 'https://submissions.signmakerz.com/api/submit-form', true);

    try {
      var emailEl = document.getElementById(cfg.emailFieldId || 'email');
      var nameEl  = document.getElementById(cfg.nameFieldId  || 'name');
      var email = (emailEl && emailEl.value) || '';
      var name  = (nameEl  && nameEl.value)  || '';
      if (typeof window._generateFormSig === 'function') {
        var sig = window._generateFormSig(email, name);
        xhr.setRequestHeader('x-form-signature', sig.signature);
        xhr.setRequestHeader('x-form-timestamp', sig.timestamp.toString());
      }
    } catch (e) { /* signature optional */ }

    function finish() {
      try { if (typeof cfg.onDone === 'function') cfg.onDone(form); } catch (e) {}
    }
    xhr.onload = finish;
    xhr.onerror = finish;
    xhr.send(formData);
  }

  function resetLeadFormState() {
    // Reset ONLY formOpenedAt. maxScroll intentionally accumulates over the
    // page's whole life — "did the visitor ever scroll on this page" is a
    // page-level signal, not a per-fill-cycle one. Wiping it here would
    // false-flag real users who scrolled once, submitted, went back to edit,
    // and re-submitted without scrolling further.
    if (window.leadFormState) {
      window.leadFormState.formOpenedAt = Date.now();
    } else {
      window.leadFormState = { formOpenedAt: Date.now(), maxScroll: 0 };
    }
  }

  window.SpamGate = {
    isSpamSubmission: isSpamSubmission,
    evaluateSpam: evaluateSpam,
    attachDiagnosticMeta: attachDiagnosticMeta,
    submitFormB: submitFormB,
    resetLeadFormState: resetLeadFormState,
    hasAdParams: hasAdParams,
    getScrollDepth: getScrollDepth,
  };
})();
